<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed, ref } from 'vue';

  import type { Partner, Task } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
  } from '@ui/components/command';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import BulkTaskDismissAlert from '@/components/tasks/BulkTaskDismissAlert.vue';
  import { useTaskCollectionStore } from '@/store';

  interface Action {
    icon: string;
    label: string;
    value: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    buttonText: 'Bulk Actions',
  });

  const emit = defineEmits<{
    (e: 'clearSelection'): void;
  }>();

  const taskCollectionStore = useTaskCollectionStore();
  const taskIds = computed(() => props.tasks?.map((task) => task.id));
  const taskCount = computed(() => props.tasks?.length);
  const open = ref(false);
  const isBulkAcknowledging = ref(false);

  const handleBulkAcknowledge = async () => {
    if (isBulkAcknowledging.value) return;

    isBulkAcknowledging.value = true;

    try {
      await stuut.tasks.bulkCompleteAction(taskIds.value, 'acknowledge', undefined, props.tasks);

      toast.success(`Successfully acknowledged ${pluralize('task', taskCount.value, true)}`);
      emit('clearSelection');
      taskCollectionStore.refreshAllTaskTables();
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred while acknowledging tasks');
    } finally {
      isBulkAcknowledging.value = false;
    }
  };

  interface Props {
    buttonText?: string;
    partner?: Partner;
    showBulkAcknowledge?: boolean;
    tasks: Task[];
  }

  const showDismissAlert = ref<boolean>(false);
  const dismissAlertActionType = ref<string | undefined>();

  const closeDismissAlert = () => {
    showDismissAlert.value = false;
    dismissAlertActionType.value = undefined;
  };

  const openDismissAlert = (actionType?: string) => {
    showDismissAlert.value = true;
    dismissAlertActionType.value = actionType;
  };

  const selectCommand = (action) => {
    open.value = false;

    return openDismissAlert(action.value);
  };

  const bulkActions: Action[] = [
    {
      icon: 'fa-circle-xmark text-destructive',
      label: 'Close',
      value: 'close',
    },
    {
      icon: 'fa-flag text-agent',
      label: 'Mark Inaccurate',
      value: 'mark_inaccurate',
    },
  ];
</script>

<template>
  <div class="flex flex-row items-center gap-x-2">
    <ConfirmDialog
      v-if="props.showBulkAcknowledge"
      confirm-text="Acknowledge"
      @confirm="handleBulkAcknowledge"
    >
      <template #trigger>
        <Button
          :disabled="!tasks?.length || isBulkAcknowledging"
          :loading="isBulkAcknowledging"
          size="sm"
        >
          <i class="fas fa-thumbs-up text-xs" />
          Acknowledge!
        </Button>
      </template>
      <template #title>Confirm Bulk Acknowledge</template>
      <template #description>
        <p>
          Are you sure you want to acknowledge
          {{ pluralize('task', taskCount, true) }}? This action cannot be undone.
        </p>
      </template>
    </ConfirmDialog>

    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button variant="outline" size="sm" class="w-fit justify-start" :disabled="!tasks?.length">
          {{ buttonText }} <i class="fa-solid fa-caret-down"></i>
        </Button>
      </PopoverTrigger>

      <PopoverContent class="p-0 w-[150px]" side="right" align="start">
        <Command>
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                v-for="action in bulkActions"
                :key="action.value"
                class="space-x-2 px-2"
                :value="action.value"
                @select="selectCommand(action)"
              >
                <i class="fa-solid text-2xs" :class="action.icon"></i>
                <span class="text-xs">{{ action.label }}</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>

    <BulkTaskDismissAlert
      v-if="props.tasks?.length"
      :tasks="props.tasks"
      :show="showDismissAlert"
      :action-type="dismissAlertActionType"
      @close="closeDismissAlert"
      @clear-selection="emit('clearSelection')"
    />
  </div>
</template>

<style scoped></style>
